<template>
  <div class="flex flex-col items-center w-full">
    <!-- Chord name -->
    <div class="text-sm font-bold mb-1.5 text-gray-900 dark:text-white">{{ chordName }}</div>

    <!-- SVG diagram -->
    <svg
      :width="svgWidth"
      :height="svgHeight"
      :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
      xmlns="http://www.w3.org/2000/svg"
      class="overflow-visible"
    >
      <!-- Open/Muted string markers (top row) -->
      <g v-for="(fret, i) in position.frets" :key="'top-' + i">
        <!-- Muted -->
        <text
          v-if="fret === -1"
          :x="gridLeft + i * colW"
          :y="topRowY"
          text-anchor="middle"
          dominant-baseline="middle"
          font-size="9"
          font-family="sans-serif"
          :fill="dotColor"
        >×</text>
        <!-- Open -->
        <circle
          v-else-if="fret === 0"
          :cx="gridLeft + i * colW"
          :cy="topRowY"
          r="3.5"
          fill="none"
          :stroke="dotColor"
          stroke-width="1.5"
        />
      </g>

      <!-- Nut (thick bar at top fret when baseFret === 1) -->
      <rect
        v-if="position.baseFret === 1"
        :x="gridLeft - 1"
        :y="gridTop"
        :width="(numStrings - 1) * colW + 2"
        height="4"
        rx="2"
        :fill="dotColor"
      />
      <!-- Normal top fret line when baseFret > 1 -->
      <line
        v-else
        :x1="gridLeft"
        :y1="gridTop"
        :x2="gridLeft + (numStrings - 1) * colW"
        :y2="gridTop"
        :stroke="lineColor"
        stroke-width="1"
      />

      <!-- Base fret label -->
      <text
        v-if="position.baseFret > 1"
        :x="gridLeft - 8"
        :y="gridTop + rowH / 2"
        text-anchor="end"
        dominant-baseline="middle"
        font-size="8"
        font-family="sans-serif"
        :fill="dotColor"
      >{{ position.baseFret }}fr</text>

      <!-- Fret lines (horizontal) -->
      <line
        v-for="row in numFrets"
        :key="'row-' + row"
        :x1="gridLeft"
        :y1="gridTop + row * rowH"
        :x2="gridLeft + (numStrings - 1) * colW"
        :y2="gridTop + row * rowH"
        :stroke="lineColor"
        stroke-width="0.75"
      />

      <!-- String lines (vertical) -->
      <line
        v-for="s in numStrings"
        :key="'string-' + s"
        :x1="gridLeft + (s - 1) * colW"
        :y1="gridTop"
        :x2="gridLeft + (s - 1) * colW"
        :y2="gridTop + numFrets * rowH"
        :stroke="lineColor"
        stroke-width="0.75"
      />

      <!-- Barres -->
      <rect
        v-for="(barreFret, bi) in position.barres"
        :key="'barre-' + bi"
        :x="gridLeft + barreStart(barreFret) * colW - dotR"
        :y="gridTop + (barreFret - 1) * rowH + rowH / 2 - dotR"
        :width="(barreEnd(barreFret) - barreStart(barreFret)) * colW + dotR * 2"
        :height="dotR * 2"
        :rx="dotR"
        :fill="highlighted ? highlightColor : dotColor"
        opacity="0.95"
      />

      <!-- Individual finger dots -->
      <g v-for="(fret, si) in position.frets" :key="'dot-' + si">
        <g v-if="fret > 0 && !isInsideBarre(si, fret)">
          <circle
            :cx="gridLeft + si * colW"
            :cy="gridTop + (fret - 1) * rowH + rowH / 2"
            :r="dotR"
            :fill="highlighted ? highlightColor : dotColor"
          />
          <!-- Finger number label inside dot -->
          <text
            v-if="position.fingers && position.fingers[si] > 0"
            :x="gridLeft + si * colW"
            :y="gridTop + (fret - 1) * rowH + rowH / 2"
            text-anchor="middle"
            dominant-baseline="middle"
            font-size="7"
            font-family="sans-serif"
            font-weight="bold"
            fill="white"
          >{{ position.fingers[si] }}</text>
        </g>
        <!-- Finger label inside barre section -->
        <text
          v-else-if="fret > 0 && isInsideBarre(si, fret) && si === barreStart(fret) && position.fingers && position.fingers[si] > 0"
          :x="gridLeft + si * colW"
          :y="gridTop + (fret - 1) * rowH + rowH / 2"
          text-anchor="middle"
          dominant-baseline="middle"
          font-size="7"
          font-family="sans-serif"
          font-weight="bold"
          fill="white"
        >{{ position.fingers[si] }}</text>
      </g>
    </svg>

    <!-- Variation indicator -->
    <div v-if="totalVariations > 1" class="flex items-center gap-1 mt-1.5">
      <div
        v-for="v in totalVariations"
        :key="v"
        class="rounded-full transition-all duration-200"
        :class="[
          v - 1 === currentVariation
            ? 'bg-indigo-500 w-3 h-1.5'
            : 'bg-gray-300 dark:bg-gray-600 w-1.5 h-1.5'
        ]"
      />
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue'
const props = defineProps({
  chordName: { type: String, required: true },
  position: { type: Object, required: true },
  currentVariation: { type: Number, default: 0 },
  totalVariations: { type: Number, default: 1 },
  highlighted: { type: Boolean, default: false }
})

// Layout constants
const numStrings = computed(() => props.position?.frets?.length || 6)
const numFrets = 5
const colW = 14   // pixels between strings
const rowH = 13   // pixels between frets
const dotR = 5    // dot radius
const gridLeft = 18
const gridTop = 22
const topRowY = 12

const svgWidth = computed(() => gridLeft + (numStrings.value - 1) * colW + 14)
const svgHeight = gridTop + numFrets * rowH + 10

// Colors — uses CSS variables so dark mode works
const lineColor = '#9ca3af'  // gray-400
const dotColor = '#1f2937'   // gray-800 (overridden by CSS in dark mode)
const highlightColor = '#6366f1' // indigo-500

// Helpers
const barreStart = (barreFret) => {
  return props.position.frets.findIndex(f => f === barreFret)
}
const barreEnd = (barreFret) => {
  let last = 0
  for (let i = numStrings.value - 1; i >= 0; i--) {
    if (props.position.frets[i] === barreFret) { last = i; break }
  }
  return last
}
const isInsideBarre = (stringIndex, fret) => {
  if (!props.position.barres || !props.position.barres.includes(fret)) return false
  const start = barreStart(fret)
  const end = barreEnd(fret)
  return stringIndex >= start && stringIndex <= end
}
</script>

<style scoped>
:deep(svg text) {
  user-select: none;
}
.dark svg line,
.dark svg rect.fret-line {
  stroke: #6b7280;
}
</style>
